/**
 * Pure state machine for the dialogue overlay (Future Cartridge PR 2).
 *
 * Lives in its own module so unit tests can exercise the transitions
 * in vitest Node — the Phaser-wired shell at apps/game/src/ui/
 * dialogueOverlay.ts is the impure consumer (DOM mount, animations,
 * gesture wiring, viewport-resize handling). Mirrors the established
 * partyOrbsLayout.ts / PartyOrbs.ts split.
 *
 * The five-state enum encodes the entry/exit animation lifecycle so
 * the shell can mount/unmount + start/cancel CSS keyframes from a
 * single source of truth. `suppressed` is the catch-all "scene is
 * gone, DOM is hidden" state used during Phaser SLEEP — distinct from
 * `closed` because re-mount semantics differ (a sleep-suppressed
 * dialogue does NOT auto-reopen on wake; the WorldScene re-derives
 * what to render from runState).
 *
 * PartySheet suppression is handled via shouldSuppress() WITHOUT a
 * state transition — PartySheetScene is a transparent overlay that
 * doesn't sleep WorldScene, so the dialogue stays logically open and
 * the DOM is just hidden via display:none. On PartySheet close, the
 * dialogue restores to the same page.
 */

export type DialogState = 'closed' | 'opening' | 'open' | 'closing' | 'suppressed';

export type DialogEvent = 'OPEN' | 'CLOSE' | 'ENTRY_COMPLETE' | 'EXIT_COMPLETE';

/**
 * Reduce the dialogue state given a lifecycle event.
 *
 *   closed   + OPEN            → opening
 *   opening  + CLOSE           → closing      (cancel mid-entry)
 *   opening  + ENTRY_COMPLETE  → open
 *   open     + CLOSE           → closing
 *   closing  + OPEN            → opening      (cancel mid-close)
 *   closing  + EXIT_COMPLETE   → closed
 *   suppressed + *             → suppressed   (events blocked until wake)
 *
 * All other state×event pairs are no-ops (the current state is
 * returned unchanged). OPEN on `open`, CLOSE on `closed`, etc. are
 * idempotent. Suppressed state hard-blocks OPEN/CLOSE — only
 * nextStateOnWake() can leave it.
 */
export function reduceDialog(state: DialogState, event: DialogEvent): DialogState {
  switch (state) {
    case 'closed':
      return event === 'OPEN' ? 'opening' : 'closed';
    case 'opening':
      if (event === 'CLOSE') return 'closing';
      if (event === 'ENTRY_COMPLETE') return 'open';
      return 'opening';
    case 'open':
      return event === 'CLOSE' ? 'closing' : 'open';
    case 'closing':
      if (event === 'OPEN') return 'opening';
      if (event === 'EXIT_COMPLETE') return 'closed';
      return 'closing';
    case 'suppressed':
      return 'suppressed';
  }
}

/**
 * Should the dialogue DOM be hidden (display: none) right now?
 *
 * True when:
 *  - Logical state is `closed` or `suppressed` (no visible chrome).
 *  - PartySheet is open (transparent overlay; dialogue hides under it).
 *  - WorldScene is sleeping (Phaser SLEEP → DOM hidden).
 *
 * The Phaser-wired shell calls this on every state change AND on
 * external context changes (PartySheet open/close, sleep/wake). Mid-
 * animation states (`opening`, `closing`) DO render — they need DOM
 * presence for the CSS keyframes to play.
 */
export function shouldSuppress(
  state: DialogState,
  partySheetOpen: boolean,
  sleeping: boolean,
): boolean {
  if (sleeping || partySheetOpen) return true;
  return state === 'closed' || state === 'suppressed';
}

/**
 * Transition fired when Phaser SLEEP lands on WorldScene (battle
 * launches, draft modal opens, etc.). Any state → `suppressed`. The
 * shell additionally cancels any pending entry/exit animation timer
 * and hides the DOM.
 *
 * We DON'T preserve the prior state — sleep is a hard reset. On wake,
 * WorldScene re-derives what dialogue (if any) should be re-mounted
 * from runState; an auto-restore would risk re-opening a dialogue
 * whose triggering NPC no longer exists or whose page index drifted.
 */
export function nextStateOnSleep(_state: DialogState): DialogState {
  return 'suppressed';
}

/**
 * Transition fired when Phaser WAKE lands on WorldScene. Goes from
 * `suppressed` → `closed`. WorldScene then re-mounts the dialogue if
 * needed (driven by the active runState, not by a remembered prior).
 *
 * If wake fires while state is anything other than `suppressed`, the
 * current state is returned unchanged (no-op — wake without a prior
 * sleep is a defensive case that shouldn't occur in normal flow).
 */
export function nextStateOnWake(state: DialogState): DialogState {
  return state === 'suppressed' ? 'closed' : state;
}
